// src/App.tsx
import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Spin } from 'antd';

import AppRoutes from './routes';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { initializeAuth } from './store/slices/authSlice';
import ErrorBoundary from './components/ErrorBoundary';
import { setFavicon } from './utils/favicon';
import { initMsal } from './utils/msal';
import './App.css';
import 'antd/dist/reset.css';

function App() {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);
  const { initialized, loading, isAuthenticated, user, token } = authState;
  const faviconAppliedForToken = useRef<string | null>(null);

  useEffect(() => {
    initMsal();
  }, []);

  useEffect(() => {
    // Initialize authentication on app startup
    if (!initialized && !loading) {
      dispatch(initializeAuth());
    }
  }, [dispatch, initialized, loading]);

  useEffect(() => {
    // Keep favicon in sync with the organization logo once authenticated
    if (!initialized || !isAuthenticated || !token) {
      faviconAppliedForToken.current = null;
      setFavicon();
      return;
    }

    if (faviconAppliedForToken.current === token) {
      return;
    }

    faviconAppliedForToken.current = token;

    if (user?.organization?.favicon_url || user?.organization?.logo_url) {
      setFavicon(user.organization.favicon_url || user.organization.logo_url);
    }
  }, [initialized, isAuthenticated, token, user]);

  // Show loading spinner while initializing authentication
  if (!initialized && loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* <AppLayout> */}
        <AppRoutes />
        {/* </AppLayout> */}
      </Router>
    </ErrorBoundary>
  );
}

export default App;
